import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Clock, Camera, X } from "lucide-react";
import useUsage from "@/hooks/useUsage";
import { useApp } from "@/state/AppContext";

/**
 * One-time welcome banner shown to freshly registered users. Explains the
 * 7-day trial + 2-document cap and CTAs them straight into the flow.
 * Dismisses itself after user clicks the X or navigates elsewhere.
 */
export default function WelcomeBanner() {
  const usage = useUsage();
  const { user } = useApp();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    let flag = null;
    try { flag = sessionStorage.getItem("ba_just_registered"); } catch (_) { void 0; }
    if (flag === "1") setVisible(true);
  }, [user]);

  if (!visible || usage.tier !== "free" || usage.trial_expired) return null;

  const daysLeft = usage.trial_expires_at
    ? Math.max(0, Math.ceil((new Date(usage.trial_expires_at).getTime() - Date.now()) / (24 * 3600 * 1000)))
    : usage.trial_days_total;

  const dismiss = () => {
    try { sessionStorage.removeItem("ba_just_registered"); } catch (_) { void 0; }
    setVisible(false);
  };

  return (
    <div className="rounded-3xl bg-blue-600 text-white p-4 shadow-md shadow-blue-600/25 relative overflow-hidden"
         data-testid="welcome-banner">
      <button
        onClick={dismiss}
        data-testid="welcome-dismiss"
        aria-label="Schließen"
        className="absolute right-2 top-2 p-1.5 rounded-full hover:bg-white/15"
      >
        <X className="w-4 h-4 text-white" />
      </button>
      <div className="flex items-center gap-2 text-white/90 text-xs font-semibold uppercase tracking-wider">
        <Sparkles className="w-4 h-4 text-amber-300" /> Willkommen an Bord
      </div>
      <h2 className="mt-1 text-xl font-bold">Deine Testphase läuft.</h2>
      <p className="mt-1 text-white/95 text-sm leading-relaxed">
        Du kannst {usage.limit - usage.used} von {usage.limit} Dokumenten kostenlos analysieren.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1 flex items-center gap-1.5 text-xs bg-white/10 rounded-full px-3 py-1.5">
          <Clock className="w-3.5 h-3.5" />
          {daysLeft === 0 ? "Letzter Tag" : daysLeft === 1 ? "Noch 1 Tag" : `Noch ${daysLeft} Tage`}
        </div>
        <button
          onClick={() => { dismiss(); navigate("/upload"); }}
          data-testid="welcome-cta"
          className="flex items-center gap-1.5 bg-white text-blue-700 px-4 py-2 rounded-full text-sm font-semibold active:scale-[0.97] transition"
        >
          <Camera className="w-4 h-4" /> Ersten Brief prüfen
        </button>
      </div>
    </div>
  );
}
